import { defineComponent, h, nextTick, Transition } from 'vue';
import debounce from 'lodash/debounce';
import { marked } from 'marked';
import BaseComponent from './baseFormMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { Code } from '@tiptap/extension-code';
import { Markdown } from '@tiptap/markdown';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { Image } from '@tiptap/extension-image';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
    renderTableToMarkdown,
} from '@tiptap/extension-table';
import {
    QBtn,
    QSeparator,
    QSelect,
    QMenu,
    QList,
    QItem,
    QItemSection,
    QInput,
} from 'quasar';
import './formMarkdownEditor.css';

// Default TipTap Code uses excludes: '_' (no other marks on the same text). GFM often nests
// emphasis inside codespan (e.g. **`Height`**), which @tiptap/markdown parses as bold+code.
const InlineCode = Code.extend({ excludes: '' });

/** GFM splits table rows on `|`; escape literals so e.g. `string` \| `null` round-trips. */
function escapeMarkdownTableCellPipes(s) {
    return typeof s === 'string' ? s.replace(/\|/g, '\\|') : s;
}

const TableWithEscapedCellPipes = Table.extend({
    renderMarkdown: (node, h) => {
        const hWrap = Object.assign({}, h, {
            renderChildren(...args) {
                return escapeMarkdownTableCellPipes(h.renderChildren(...args));
            },
        });
        return renderTableToMarkdown(node, hWrap);
    },
});

const HEADING_OPTIONS = [
    { label: 'Normal', value: null },
    { label: 'Heading 1', value: 1 },
    { label: 'Heading 2', value: 2 },
    { label: 'Heading 3', value: 3 },
    { label: 'Heading 4', value: 4 },
    { label: 'Heading 5', value: 5 },
    { label: 'Heading 6', value: 6 },
];

function buildExtensions() {
    return [
        // Inline code must register before StarterKit's Bold so ProseMirror mark rank is
        // code < bold. @tiptap/markdown opens marks in rank order (inner first); wrong
        // order yields `` `**text**` `` instead of `` **`text`** `` for bold + code.
        InlineCode,
        StarterKit.configure({
            heading: { levels: [1, 2, 3, 4, 5, 6] },
            underline: false,
            code: false,
            link: {
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                HTMLAttributes: { rel: 'noopener noreferrer' },
            },
        }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Image.configure({
            inline: false,
            allowBase64: false,
            resize: false,
        }),
        TableWithEscapedCellPipes.configure({ resizable: false }),
        TableCell,
        TableHeader,
        TableRow,
        Markdown.configure({
            markedOptions: { gfm: true, breaks: true },
        }),
    ];
}

export default defineComponent({
    name: 'sform-markdowneditor',
    mixins: [BaseComponent],
    components: {
        EditorContent,
        Transition,
        QBtn,
        QSeparator,
        QSelect,
        QMenu,
        QList,
        QItem,
        QItemSection,
        QInput,
    },
    data: () => ({
        readOnlyEval: null,
        hasfocus: false,
        height: '150px',
        minHeight: '150px',
        maxHeight: undefined,
        editor: null,
        toolbarRevision: 0,
        menuTable: false,
        menuLink: false,
        linkDraft: '',
        menuImage: false,
        imageSrcDraft: '',
        imageAltDraft: '',
        lastSyncedMarkdown: '',
        rawMode: false,
        rawDraft: '',
        rawFocused: false,
        _debouncedSync: null,
        _unwatchItemvalue: null,
        _unwatchReadonly: null,
    }),
    created() {
        const initialSize = this.resolveInitialSize();
        this.height = initialSize.height;
        this.minHeight = initialSize.minHeight;
        if (initialSize.maxHeight)
            this.maxHeight = initialSize.maxHeight;

        if (!this.noinput) {
            this._debouncedSync = debounce((md) => {
                this.lastSyncedMarkdown = md;
                this.sync(md);
            }, 150);
        }
    },
    mounted() {
        this.loadComplete();
        if (this.noinput)
            return;

        const initialMd = (this.itemvalue ?? '').toString();
        this.lastSyncedMarkdown = initialMd;

        this.editor = new Editor({
            extensions: buildExtensions(),
            content: initialMd,
            contentType: 'markdown',
            editable: !this.readOnly,
            editorProps: {
                attributes: {
                    class: 'form-markdown-editor-prose tiptap',
                },
            },
            onUpdate: () => {
                this.onEditorDocChange();
            },
            onSelectionUpdate: () => {
                this.toolbarRevision++;
            },
            onTransaction: () => {
                this.toolbarRevision++;
            },
            onFocus: () => {
                this.hasfocus = true;
            },
            onBlur: () => {
                this.hasfocus = false;
                this.flushMarkdownToModel();
            },
        });

        this._unwatchItemvalue = this.$watch(
            () => this.itemvalue,
            (val) => {
                if (!this.editor)
                    return;
                const next = (val ?? '').toString();
                if (next === this.lastSyncedMarkdown)
                    return;
                if (this.rawMode) {
                    if (this.rawFocused)
                        return;
                    this.rawDraft = next;
                    this.lastSyncedMarkdown = next;
                    return;
                }
                if (this.hasfocus)
                    return;
                this.editor.commands.setContent(next, {
                    contentType: 'markdown',
                    emitUpdate: false,
                });
                this.lastSyncedMarkdown = next;
            }
        );

        this._unwatchReadonly = this.$watch(
            'readOnly',
            (ro) => {
                if (!this.editor)
                    return;
                if (this.rawMode)
                    this.editor.setEditable(false);
                else
                    this.editor.setEditable(!ro);
            }
        );

        if (!this.isFillHeightMode())
            nextTick(() => this.attachWrapperResizeObserver());
    },
    beforeUnmount() {
        if (this._debouncedSync) {
            this._debouncedSync.cancel();
            this._debouncedSync = null;
        }
        if (this._unwatchItemvalue) {
            this._unwatchItemvalue();
            this._unwatchItemvalue = null;
        }
        if (this._unwatchReadonly) {
            this._unwatchReadonly();
            this._unwatchReadonly = null;
        }
        this.detachWrapperResizeObserver();
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }
    },
    computed: {
        readOnly() {
            try {
                if (this.readOnlyEval === null && this.element.readonly)
                    this.readOnlyEval = utils.compile(this, this.element.readonly);
                if (this.readOnlyEval)
                    return utils.evaluate(this.readOnlyEval, this);
                return false;
            }
            catch (e) {
                utils.warn('ReadOnly could not evaluate expression: ' + this.element.readonly + '; ' + e);
                return false;
            }
        },
        /**
         * Display-only HTML when noinput (same marked options as @tiptap/markdown config).
         * Model HTML is trusted like other schema markdown surfaces (e.g. Text.jsx); add sanitization if needed.
         */
        displayHtml() {
            const raw = (this.itemvalue ?? '').toString();
            if (!raw)
                return '';
            try {
                return marked.parse(raw, { async: false, gfm: true, breaks: true });
            }
            catch (e) {
                utils.warn('formMarkdownEditor displayHtml (marked) failed: ' + e);
                return '';
            }
        },
        noinputContainerStyle() {
            const s = {
                width: '100%',
                boxSizing: 'border-box',
            };
            if (this.minHeight)
                s.minHeight = this.minHeight;
            if (this.maxHeight)
                s.maxHeight = this.maxHeight;
            if (this.height && this.element?.formatData?.SizeOptions?.Height?.Mode !== 'Fill')
                s.height = this.height;
            if (this.isFillHeightMode()) {
                s.flexGrow = '1';
                s.alignSelf = 'normal';
            }
            return s;
        },
    },
    methods: {
        isFillHeightMode() {
            return this.element?.formatData?.SizeOptions?.Height?.Mode === 'Fill';
        },
        resolveInitialSize() {
            if (this.element.formatData && this.element.formatData.SizeOptions) {
                const h = this.element.formatData.SizeOptions.Height;
                switch (h.Mode) {
                    case 'Auto':
                        return {
                            height: (h.Min || '150') + 'px',
                            minHeight: (h.Min || '150') + 'px',
                            maxHeight: h.Max ? (h.Max + 'px') : undefined
                        };
                    case 'Fixed':
                        return {
                            height: h.Value + 'px',
                            minHeight: h.Value + 'px'
                        };
                    case 'Percent':
                        return { height: h.Value + '%', minHeight: h.Value + '%' };
                    case 'Fill':
                        return {
                            height: 'auto',
                            minHeight: (h.Min || '150') + 'px',
                            maxHeight: h.Max ? (h.Max + 'px') : undefined
                        };
                    default:
                        return { height: '150px', minHeight: '150px' };
                }
            }
            return { height: '150px', minHeight: '150px' };
        },
        attachWrapperResizeObserver() {
            if (typeof ResizeObserver === 'undefined')
                return;
            const host = this.$refs.editorWrapperHost;
            if (!host)
                return;
            this.detachWrapperResizeObserver();
            this._wrapperResizeObserver = new ResizeObserver(() => {
                const px = Math.round(host.offsetHeight || 0);
                if (!px)
                    return;
                const next = `${px}px`;
                if (this.height !== next)
                    this.height = next;
            });
            this._wrapperResizeObserver.observe(host);
        },
        detachWrapperResizeObserver() {
            if (this._wrapperResizeObserver) {
                this._wrapperResizeObserver.disconnect();
                this._wrapperResizeObserver = null;
            }
        },
        onEditorDocChange() {
            if (!this.editor)
                return;
            try {
                const md = this.editor.getMarkdown();
                this._debouncedSync(md);
            }
            catch (e) {
                utils.warn('formMarkdownEditor getMarkdown failed: ' + e);
            }
            this.toolbarRevision++;
        },
        flushMarkdownToModel() {
            if (!this.editor)
                return;
            if (this._debouncedSync && this._debouncedSync.flush)
                this._debouncedSync.flush();
            try {
                const md = this.editor.getMarkdown();
                this.lastSyncedMarkdown = md;
                this.sync(md);
            }
            catch (e) {
                utils.warn('formMarkdownEditor flush markdown failed: ' + e);
            }
        },
        chain() {
            return this.editor?.chain().focus();
        },
        tbDisabled() {
            return this.readOnly || !this.editor;
        },
        tipTapDisabled() {
            return this.tbDisabled() || this.rawMode;
        },
        enterRawMode() {
            if (!this.editor || this.readOnly)
                return;
            this.menuTable = false;
            this.menuLink = false;
            this.menuImage = false;
            this.flushMarkdownToModel();
            let md = '';
            try {
                md = this.editor.getMarkdown();
            }
            catch (e) {
                utils.warn('formMarkdownEditor enterRawMode getMarkdown failed: ' + e);
                return;
            }
            this.rawDraft = md;
            this.rawMode = true;
            this.editor.setEditable(false);
            this.toolbarRevision++;
        },
        exitRawMode() {
            if (!this.editor)
                return;
            if (this._debouncedSync && this._debouncedSync.flush)
                this._debouncedSync.flush();
            const md = this.rawDraft;
            try {
                this.editor.commands.setContent(md, {
                    contentType: 'markdown',
                    emitUpdate: false,
                });
            }
            catch (e) {
                utils.warn('formMarkdownEditor exitRawMode setContent failed: ' + e);
            }
            this.lastSyncedMarkdown = md;
            this.sync(md);
            this.editor.setEditable(!this.readOnly);
            this.rawMode = false;
            this.toolbarRevision++;
            nextTick(() => {
                this.editor?.commands.focus();
            });
        },
        toggleRawMode() {
            if (this.rawMode)
                this.exitRawMode();
            else
                this.enterRawMode();
        },
        onRawDraftUpdate(v) {
            this.rawDraft = v;
            if (this._debouncedSync)
                this._debouncedSync(v);
        },
        onRawFocus() {
            this.rawFocused = true;
            this.hasfocus = true;
        },
        onRawBlur() {
            this.rawFocused = false;
            this.hasfocus = false;
            this.flushRawToModel();
        },
        flushRawToModel() {
            if (!this.rawMode)
                return;
            if (this._debouncedSync && this._debouncedSync.flush)
                this._debouncedSync.flush();
            const md = this.rawDraft;
            this.lastSyncedMarkdown = md;
            this.sync(md);
        },
        async pasteMarkdownFromClipboard() {
            if (!this.editor || this.readOnly)
                return;
            try {
                // Clipboard.readText() requires a focused document; toolbar clicks often leave focus on the button.
                if (typeof window !== 'undefined' && typeof window.focus === 'function')
                    window.focus();
                this.editor.chain().focus().run();
                await nextTick();
                const text = await navigator.clipboard.readText();
                if (!text || !text.trim())
                    return;
                this.editor.chain().focus().insertContent(text, { contentType: 'markdown' }).run();
            }
            catch (e) {
                utils.warn('formMarkdownEditor paste markdown from clipboard failed: ' + e);
            }
        },
        prepareLinkMenu() {
            if (!this.editor)
                return;
            const prev = this.editor.getAttributes('link').href || '';
            this.linkDraft = typeof prev === 'string' ? prev : '';
        },
        applyLink() {
            const url = (this.linkDraft || '').trim();
            if (!this.editor)
                return;
            if (!url)
                this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
            else
                this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            this.menuLink = false;
        },
        prepareImageMenu() {
            if (!this.editor)
                return;
            const attrs = this.editor.getAttributes('image');
            this.imageSrcDraft = typeof attrs.src === 'string' ? attrs.src : '';
            this.imageAltDraft = typeof attrs.alt === 'string' ? attrs.alt : '';
        },
        applyImage() {
            const src = (this.imageSrcDraft || '').trim();
            const alt = (this.imageAltDraft || '').trim();
            if (!this.editor)
                return;
            if (!src) {
                if (this.editor.isActive('image'))
                    this.editor.chain().focus().deleteNode('image').run();
            }
            else if (this.editor.isActive('image'))
                this.editor.chain().focus().updateAttributes('image', { src, alt: alt || null }).run();
            else
                this.editor.chain().focus().setImage({ src, alt: alt || undefined }).run();
            this.menuImage = false;
        },
        removeImage() {
            if (!this.editor)
                return;
            if (this.editor.isActive('image'))
                this.editor.chain().focus().deleteNode('image').run();
            this.menuImage = false;
        },
        currentHeadingValue() {
            const ed = this.editor;
            if (!ed)
                return null;
            for (let level = 1; level <= 6; level++) {
                if (ed.isActive('heading', { level }))
                    return level;
            }
            return null;
        },
        onHeadingSelect(level) {
            const c = this.chain();
            if (!c)
                return;
            if (level == null)
                c.setParagraph().run();
            else
                c.toggleHeading({ level }).run();
        },
        sinkListItem() {
            const c = this.chain();
            const ed = this.editor;
            if (!c || !ed)
                return;
            if (ed.can().sinkListItem('taskItem'))
                c.sinkListItem('taskItem').run();
            else
                c.sinkListItem('listItem').run();
        },
        liftListItem() {
            const c = this.chain();
            const ed = this.editor;
            if (!c || !ed)
                return;
            if (ed.can().liftListItem('taskItem'))
                c.liftListItem('taskItem').run();
            else
                c.liftListItem('listItem').run();
        },
        canSinkListItem() {
            const ed = this.editor;
            if (!ed)
                return false;
            return ed.can().sinkListItem('taskItem') || ed.can().sinkListItem('listItem');
        },
        canLiftListItem() {
            const ed = this.editor;
            if (!ed)
                return false;
            return ed.can().liftListItem('taskItem') || ed.can().liftListItem('listItem');
        },
        renderToolbar() {
            const ed = this.editor;
            const dis = this.tbDisabled();
            const ttd = this.tipTapDisabled();
            void this.toolbarRevision;

            const inTable = !!(ed && ed.isActive('table'));
            const canMerge = !!(ed && ed.can().mergeCells());
            const canSplit = !!(ed && ed.can().splitCell());
            const showTableSecondarySection = inTable || canMerge || canSplit;

            const iconBtn = (icon, onClick, active = false, extra = {}) => {
                const { disable: extraDisable, ...rest } = extra;
                return (
                    <QBtn
                        flat={!active}
                        unelevated={active}
                        dense
                        round
                        size="sm"
                        color={active ? 'primary' : 'grey-8'}
                        icon={icon}
                        disable={ttd || !!extraDisable}
                        onClick={onClick}
                        {...rest}
                    />
                );
            };

            return (
                <div class="form-markdown-editor-toolbar row items-center no-wrap">
                    {iconBtn('mdi-undo', () => this.chain()?.undo().run(), false, {
                        disable: ttd || !ed?.can().undo(),
                        title: 'Undo',
                        'aria-label': 'Undo',
                    })}
                    {iconBtn('mdi-redo', () => this.chain()?.redo().run(), false, {
                        disable: ttd || !ed?.can().redo(),
                        title: 'Redo',
                        'aria-label': 'Redo',
                    })}
                    {iconBtn('mdi-format-header-pound', () => { void this.pasteMarkdownFromClipboard(); }, false, {
                        title: 'Paste markdown from clipboard (inserts as formatted text)',
                        'aria-label': 'Paste markdown from clipboard as formatted text',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-bold', () => this.chain()?.toggleBold().run(), ed?.isActive('bold'), {
                        title: 'Bold',
                        'aria-label': 'Bold',
                    })}
                    {iconBtn('mdi-format-italic', () => this.chain()?.toggleItalic().run(), ed?.isActive('italic'), {
                        title: 'Italic',
                        'aria-label': 'Italic',
                    })}
                    {iconBtn('mdi-format-strikethrough', () => this.chain()?.toggleStrike().run(), ed?.isActive('strike'), {
                        title: 'Strikethrough',
                        'aria-label': 'Strikethrough',
                    })}
                    {iconBtn('mdi-code-tags', () => this.chain()?.toggleCode().run(), ed?.isActive('code'), {
                        title: 'Inline code',
                        'aria-label': 'Inline code',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QSelect
                        dense
                        borderless
                        options-dense
                        emit-value
                        map-options
                        options={HEADING_OPTIONS}
                        modelValue={this.currentHeadingValue()}
                        disable={ttd}
                        color={this.currentHeadingValue() != null ? 'primary' : 'grey-8'}
                        onUpdate:modelValue={(v) => this.onHeadingSelect(v)}
                        title="Paragraph or heading level"
                        aria-label="Paragraph or heading level"
                    />
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-list-bulleted', () => this.chain()?.toggleBulletList().run(), ed?.isActive('bulletList'), {
                        title: 'Bullet list',
                        'aria-label': 'Bullet list',
                    })}
                    {iconBtn('mdi-format-list-numbered', () => this.chain()?.toggleOrderedList().run(), ed?.isActive('orderedList'), {
                        title: 'Numbered list',
                        'aria-label': 'Numbered list',
                    })}
                    {iconBtn('mdi-format-list-checks', () => this.chain()?.toggleTaskList().run(), ed?.isActive('taskList'), {
                        title: 'Task list',
                        'aria-label': 'Task list',
                    })}
                    {iconBtn('mdi-format-indent-decrease', () => this.liftListItem(), false, {
                        disable: ttd || !this.canLiftListItem(),
                        title: 'Outdent list item',
                        'aria-label': 'Outdent list item',
                    })}
                    {iconBtn('mdi-format-indent-increase', () => this.sinkListItem(), false, {
                        disable: ttd || !this.canSinkListItem(),
                        title: 'Indent list item',
                        'aria-label': 'Indent list item',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-quote-close', () => this.chain()?.toggleBlockquote().run(), ed?.isActive('blockquote'), {
                        title: 'Blockquote',
                        'aria-label': 'Blockquote',
                    })}
                    {iconBtn('mdi-code-braces-box', () => this.chain()?.toggleCodeBlock().run(), ed?.isActive('codeBlock'), {
                        title: 'Code block',
                        'aria-label': 'Code block',
                    })}
                    {iconBtn('mdi-minus', () => this.chain()?.setHorizontalRule().run(), false, {
                        title: 'Horizontal rule',
                        'aria-label': 'Horizontal rule',
                    })}
                    {iconBtn('mdi-keyboard-return', () => this.chain()?.setHardBreak().run(), false, {
                        disable: ttd || !ed?.can().setHardBreak(),
                        title: 'Line break',
                        'aria-label': 'Line break',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QBtn
                        flat={!ed?.isActive('link')}
                        unelevated={!!ed?.isActive('link')}
                        dense
                        round
                        size="sm"
                        color={ed?.isActive('link') ? 'primary' : 'grey-8'}
                        icon="mdi-link-variant"
                        disable={ttd}
                        title="Insert or edit link"
                        aria-label="Insert or edit link"
                    >
                        <QMenu
                            modelValue={this.menuLink}
                            onUpdate:modelValue={(v) => { this.menuLink = v; }}
                            onBeforeShow={() => this.prepareLinkMenu()}
                        >
                            <div class="q-pa-md" style={{ minWidth: '260px' }}>
                                <QInput dense outlined label="URL" modelValue={this.linkDraft} onUpdate:modelValue={(v) => { this.linkDraft = v; }} />
                                <div class="row justify-end q-gutter-sm q-mt-sm">
                                    <QBtn flat label="Remove" color="negative" size="sm" onClick={() => { this.linkDraft = ''; this.applyLink(); }} />
                                    <QBtn unelevated color="primary" label="Apply" size="sm" onClick={() => this.applyLink()} />
                                </div>
                            </div>
                        </QMenu>
                    </QBtn>
                    {iconBtn('mdi-link-variant-off', () => this.chain()?.extendMarkRange('link').unsetLink().run(), false, {
                        disable: ttd || !ed?.isActive('link'),
                        title: 'Remove link',
                        'aria-label': 'Remove link',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QBtn
                        flat={!ed?.isActive('image')}
                        unelevated={!!ed?.isActive('image')}
                        dense
                        round
                        size="sm"
                        color={ed?.isActive('image') ? 'primary' : 'grey-8'}
                        icon="mdi-image-outline"
                        disable={ttd}
                        title="Insert or edit image"
                        aria-label="Insert or edit image"
                    >
                        <QMenu
                            modelValue={this.menuImage}
                            onUpdate:modelValue={(v) => { this.menuImage = v; }}
                            onBeforeShow={() => this.prepareImageMenu()}
                        >
                            <div class="q-pa-md" style={{ minWidth: '280px' }}>
                                <QInput dense outlined class="q-mb-sm" label="Image URL" modelValue={this.imageSrcDraft} onUpdate:modelValue={(v) => { this.imageSrcDraft = v; }} />
                                <QInput dense outlined label="Alt text" modelValue={this.imageAltDraft} onUpdate:modelValue={(v) => { this.imageAltDraft = v; }} />
                                <div class="row justify-end q-gutter-sm q-mt-sm">
                                    <QBtn flat label="Remove" color="negative" size="sm" disable={!ed?.isActive('image')} onClick={() => this.removeImage()} />
                                    <QBtn unelevated color="primary" label="Apply" size="sm" onClick={() => this.applyImage()} />
                                </div>
                            </div>
                        </QMenu>
                    </QBtn>
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QBtn
                        flat={!inTable}
                        unelevated={inTable}
                        dense
                        round
                        size="sm"
                        color={inTable ? 'primary' : 'grey-8'}
                        icon="mdi-table-plus"
                        disable={ttd}
                        title="Table"
                        aria-label="Table options"
                    >
                        <QMenu
                            modelValue={this.menuTable}
                            onUpdate:modelValue={(v) => { this.menuTable = v; }}
                            onBeforeShow={() => { this.toolbarRevision++; }}
                        >
                            <QList dense style={{ minWidth: '200px' }}>
                                <QItem clickable onClick={() => { this.chain()?.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); this.menuTable = false; }}>
                                    <QItemSection>Insert 3×3 table</QItemSection>
                                </QItem>
                                {showTableSecondarySection
                                    ? <QSeparator />
                                    : null}
                                {inTable
                                    ? (
                                        [
                                            <QItem key="add-col-before" clickable onClick={() => { this.chain()?.addColumnBefore().run(); this.menuTable = false; }}>
                                                <QItemSection>Add column before</QItemSection>
                                            </QItem>,
                                            <QItem key="add-col-after" clickable onClick={() => { this.chain()?.addColumnAfter().run(); this.menuTable = false; }}>
                                                <QItemSection>Add column after</QItemSection>
                                            </QItem>,
                                            <QItem key="del-col" clickable onClick={() => { this.chain()?.deleteColumn().run(); this.menuTable = false; }}>
                                                <QItemSection>Delete column</QItemSection>
                                            </QItem>,
                                            <QItem key="add-row-before" clickable onClick={() => { this.chain()?.addRowBefore().run(); this.menuTable = false; }}>
                                                <QItemSection>Add row before</QItemSection>
                                            </QItem>,
                                            <QItem key="add-row-after" clickable onClick={() => { this.chain()?.addRowAfter().run(); this.menuTable = false; }}>
                                                <QItemSection>Add row after</QItemSection>
                                            </QItem>,
                                            <QItem key="del-row" clickable onClick={() => { this.chain()?.deleteRow().run(); this.menuTable = false; }}>
                                                <QItemSection>Delete row</QItemSection>
                                            </QItem>,
                                        ]
                                    )
                                    : null}
                                {canMerge
                                    ? <QItem clickable onClick={() => { this.chain()?.mergeCells().run(); this.menuTable = false; }}>
                                        <QItemSection>Merge cells</QItemSection>
                                    </QItem>
                                    : null}
                                {canSplit
                                    ? <QItem clickable onClick={() => { this.chain()?.splitCell().run(); this.menuTable = false; }}>
                                        <QItemSection>Split cell</QItemSection>
                                    </QItem>
                                    : null}
                                {inTable
                                    ? <QItem clickable onClick={() => { this.chain()?.deleteTable().run(); this.menuTable = false; }}>
                                        <QItemSection>Delete table</QItemSection>
                                    </QItem>
                                    : null}
                            </QList>
                        </QMenu>
                    </QBtn>
                    <QSeparator vertical inset class="q-mx-xs" />
                    {iconBtn('mdi-format-clear', () => {
                        this.chain()?.unsetAllMarks().run();
                    }, false, {
                        title: 'Clear formatting',
                        'aria-label': 'Clear formatting',
                    })}
                    <QSeparator vertical inset class="q-mx-xs" />
                    <QBtn
                        flat={!this.rawMode}
                        unelevated={this.rawMode}
                        dense
                        round
                        size="sm"
                        color={this.rawMode ? 'primary' : 'grey-8'}
                        icon={this.rawMode ? 'mdi-format-size' : 'mdi-xml'}
                        disable={dis}
                        title={this.rawMode ? 'Switch to formatted view' : 'Edit raw markdown'}
                        aria-label={this.rawMode ? 'Switch to formatted view' : 'Edit raw markdown'}
                        onClick={() => this.toggleRawMode()}
                    />
                </div>
            );
        },
    },
    render() {
        if (this.noinput) {
            return (
                <div
                    class="form-markdown-editor--noinput q-mb-sm"
                    style={this.noinputContainerStyle}
                >
                    <div
                        class="form-markdown-editor-prose tiptap"
                        v-html={this.displayHtml}
                    />
                </div>
            );
        }

        const classes = {
            'q-mb-sm': true,
        };

        const padding = this.hasfocus ? '14px 5px 2px 5px' : '14px 5px 5px 5px';

        let description;
        if (this.hasfocus && this.element.description)
            description =
                <div style="font-size: smaller; padding-left: 8px; padding-top: 2px; color: rgba(0,0,0,0.6);">
                    {this.element.description}
                </div>;

        void this.toolbarRevision;

        return (
            <div
                class={classes}
                style={{
                    width: '100%',
                    borderStyle: 'solid',
                    borderColor: 'silver',
                    borderWidth: '1px',
                    borderRadius: '8px',
                    padding: padding,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: this.isFillHeightMode() ? '1' : '0',
                    alignSelf: this.isFillHeightMode() ? 'normal' : 'auto',
                }}
            >
                <label
                    style="left: 12px; right: auto; position: absolute; transform: translateY(-19px) scale(0.85); top: 10px; transform-origin: top left; color: rgba(0,0,0,0.6); background-color: white; padding: 0 6px;"
                >
                    {this.Title}
                </label>

                <div
                    ref="editorWrapperHost"
                    style={{
                        height: this.height,
                        minHeight: this.minHeight,
                        maxHeight: this.maxHeight,
                        resize: 'vertical',
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: '1',
                        boxSizing: 'border-box',
                    }}
                >
                    {this.renderToolbar()}
                    {this.editor
                        ? (
                            <div class="form-markdown-editor-body">
                                <div
                                    v-show={!this.rawMode}
                                    class="form-markdown-editor-pane"
                                >
                                    <EditorContent
                                        editor={this.editor}
                                        style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: '0' }}
                                    />
                                </div>
                                <div
                                    v-show={this.rawMode}
                                    class="form-markdown-editor-pane form-markdown-editor-raw-host"
                                >
                                    <QInput
                                        class="form-markdown-editor-raw"
                                        type="textarea"
                                        outlined
                                        dense
                                        rows={1}
                                        readonly={this.readOnly}
                                        autogrow={false}
                                        modelValue={this.rawDraft}
                                        onUpdate:modelValue={(v) => this.onRawDraftUpdate(v)}
                                        onFocus={() => this.onRawFocus()}
                                        onBlur={() => this.onRawBlur()}
                                        input-class="form-markdown-editor-raw-input"
                                        title="Raw markdown (formatted view may normalize syntax slightly)"
                                    />
                                </div>
                            </div>
                        )
                        : <div class="form-markdown-editor-prose" style={{ color: 'rgba(0,0,0,0.4)' }}>Loading…</div>}
                </div>

                <Transition name="slide-fade">
                    {description}
                </Transition>
            </div>
        );
    },
});
